/**
 * AcceptInvite — Landing público para invitaciones al Círculo.
 *
 * Flujo:
 *  1. Recibe `/app/invite/:token`.
 *  2. Consulta `GET /api/circulo/invite/:token` (endpoint público).
 *  3. Si el usuario NO está logueado:
 *       - has_account=true  → redirige a `/login?email=…&next=/app/invite/:token`
 *       - has_account=false → redirige a `/register?email=…&next=/app/invite/:token&locked=1`
 *  4. Si YA está logueado: llama a `POST /api/circulo/invite/:token/accept`
 *     y redirige a `/app/circulo/child/:childId`.
 *  5. Si el token venció o es inválido: muestra el error con un botón de contacto.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconLoader2, IconAlertTriangle, IconHomeHeart } from "@tabler/icons-react";
import axios from "axios";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { AuthShell } from "@/pages/auth/Login";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, refreshMe } = useAuth();
  const { lang } = useI18n();

  const [state, setState] = useState({ loading: true, error: "", invite: null, accepting: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Public inspect — no auth required.
        const { data } = await axios.get(`${BACKEND}/api/circulo/invite/${token}`);
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, invite: data }));
      } catch (e) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: formatApiError(e) }));
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (state.loading || state.error || !state.invite) return;
    const { invite } = state;
    // If the user is authenticated, try to accept immediately.
    if (user) {
      // If logged-in email does not match the invite email, ask them to sign out.
      if (invite.email && user.email.toLowerCase() !== invite.email.toLowerCase()) return;
      (async () => {
        setState((s) => ({ ...s, accepting: true }));
        try {
          const { data } = await api.post(`/circulo/invite/${token}/accept`);
          await refreshMe();
          nav(`/app/circulo/child/${data.child_id}`, { replace: true });
        } catch (e) {
          setState((s) => ({ ...s, accepting: false, error: formatApiError(e) }));
        }
      })();
      return;
    }
    // Not logged in — route to login or register with prefill.
    const nextPath = encodeURIComponent(`/app/invite/${token}`);
    const emailParam = encodeURIComponent(invite.email || "");
    if (invite.has_account) {
      nav(`/login?email=${emailParam}&next=${nextPath}`, { replace: true });
    } else {
      nav(`/register?email=${emailParam}&next=${nextPath}&locked=1`, { replace: true });
    }
  }, [state, user, token, nav, refreshMe]);

  return (
    <AuthShell>
      <p className="eyebrow">Chiessa Institute · Círculo de Familia</p>
      <h1 className="font-display font-medium text-[30px] mt-2 leading-tight">
        {lang === "es" ? "Aceptando invitación" : "Accepting invitation"}
      </h1>

      {state.loading && (
        <div className="mt-8 flex items-center gap-2 text-muted text-[14px]" data-testid="invite-loading">
          <IconLoader2 size={16} className="animate-spin" />
          {lang === "es" ? "Verificando el enlace..." : "Verifying link..."}
        </div>
      )}

      {state.error && (
        <div className="mt-8 card border-l-[3px] border-l-errtext" data-testid="invite-error">
          <div className="flex items-start gap-2 text-errtext">
            <IconAlertTriangle size={16} className="mt-0.5" />
            <div>
              <p className="text-[14px] font-medium">{state.error}</p>
              <p className="text-[12.5px] text-muted mt-2">
                {lang === "es"
                  ? "Pedile a quien te invitó que reenvíe la invitación desde el expediente."
                  : "Ask the person who invited you to resend the invitation from the file."}
              </p>
            </div>
          </div>
        </div>
      )}

      {state.invite && user && state.invite.email && user.email.toLowerCase() !== state.invite.email.toLowerCase() && (
        <div className="mt-8 card border-l-[3px] border-l-warntext" data-testid="invite-wrong-account">
          <p className="text-[14px]">
            {lang === "es"
              ? `Esta invitación es para ${state.invite.email}, pero tenés la sesión abierta como ${user.email}. Cerrá sesión y volvé a este enlace con la cuenta correcta.`
              : `This invitation is for ${state.invite.email}, but you are signed in as ${user.email}. Sign out and reopen this link with the correct account.`}
          </p>
        </div>
      )}

      {state.accepting && (
        <div className="mt-8 flex items-center gap-2 text-teal-deep text-[14px]" data-testid="invite-accepting">
          <IconLoader2 size={16} className="animate-spin" />
          {lang === "es" ? "Vinculando tu acceso al expediente..." : "Linking access..."}
        </div>
      )}

      {state.invite && !user && !state.error && (
        <div className="mt-6 text-muted text-[13.5px] flex items-center gap-2">
          <IconHomeHeart size={16} className="text-teal-deep" />
          {lang === "es"
            ? `Redirigiendo a ${state.invite.has_account ? "iniciar sesión" : "crear tu cuenta"}...`
            : `Redirecting to ${state.invite.has_account ? "sign in" : "create your account"}...`}
        </div>
      )}
    </AuthShell>
  );
}
